import { z } from "zod";

import { fetchWithAuth } from "@/lib/api";
import { parseApiResponse } from "@/lib/api-validation";
import { assetChartSchema, assetSchema } from "@/schemas/asset.schema";

import type { AssetChart, Asset } from "@/schemas";

import { API_BASE } from "@/config/api";

// Response schemas
const latestPriceSchema = z.object({
  price: z.number(),
  currency: z.string(),
});

const assetsResultsSchema = z.object({
  results: z.array(assetSchema),
});

export interface LatestPrice {
  price: number;
  currency: string;
}

export class AssetService {
  static async getLatestPrice(assetId: string): Promise<LatestPrice> {
    const response = await fetchWithAuth(
      `${API_BASE}/assets/${assetId}/latest-price`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch latest price");
    }
    const data = await response.json();
    return parseApiResponse(
      latestPriceSchema,
      data,
      `AssetService.getLatestPrice(${assetId})`
    );
  }

  static async getChart(assetId: string, period?: string): Promise<AssetChart> {
    const url = new URL(`${API_BASE}/charts/${assetId}`);
    if (period) {
      url.searchParams.append("period", period);
    }
    const response = await fetchWithAuth(url.toString());
    if (!response.ok) {
      throw new Error("Failed to fetch chart");
    }
    const data = await response.json();
    return parseApiResponse(
      assetChartSchema,
      data,
      `AssetService.getChart(${assetId})`
    );
  }

  static async getAssetsByIDs(ids: string[]): Promise<Asset[]> {
    if (!ids.length) return [];
    const response = await fetchWithAuth(
      `${API_BASE}/assets/list?ids=${ids.join(",")}`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch assets");
    }
    const data = await response.json();
    const validated = parseApiResponse(
      assetsResultsSchema,
      data,
      "AssetService.getAssetsByIDs"
    );
    return validated.results || [];
  }
}
